import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import Container from './Container';
import PdfCardList from './PdfCardList';
import PdfCard from './PdfCard';
import Grid from './Grid';
import Dropzone from './Dropzone';
import Button from './Button';
import Checkbox from './Checkbox';
import Field from './Field';
import usePdfFiles from '../utils/usePdfFiles';
import useZoom from '../utils/useZoom';
import useSmallFooter from '../utils/useSmallFooter';
import { sendEvent } from '../utils/analytics';
import IconZoomIn from '../public/images/zoom-in.svg';
import IconZoomOut from '../public/images/zoom-out.svg';
import IconPages from '../public/images/pages.svg';
import IconFiles from '../public/images/files.svg';
import IconRotateAnticlockwise from '../public/images/rotate-anticlockwise.svg';
import IconRotateClockwise from '../public/images/rotate-clockwise.svg';
import IconLoader from '../public/images/loader.svg';

import {
  chunkPageRange,
  flatChunkedPageRange,
  parsePageRange,
  stringifyPageRange,
} from '../utils/page-range';

const PdfViewer = forwardRef(({
  title,
  files,
  setFiles,
  viewType: initialViewType = 'files',
  showViewSwitcher = true,
  separatable = false,
  selectable = false,
  rotatable = false,
  deletableFiles = true,
  deletablePages = false,
  requireChange = null,
  loading,
  processing,
  dropzoneProps,
  disabled,
  onSubmit,
}, ref) => {
  const { t } = useTranslation('convert-end-edit');
  const [viewType, setViewType] = useState(initialViewType);
  const [separate, setSeparate] = useState(false);
  const [selectionInputValue, setSelectionInputValue] = useState('');

  const {
    pdfFiles,
    totalPages,
    deletePages,
    rotatePages,
    rotateAllPages,
    selectPages,
    selectFile,
  } = usePdfFiles(files, setFiles);

  const { zoom, zoomIn, zoomOut } = useZoom(PdfCard.minZoom, PdfCard.maxZoom);

  // Страницы, где сабмит без изменений бессмыслен, объявляют requireChange:
  // имя поля pdfFile ('rotations' | 'deletions'), которое должно быть непустым.
  // rotate без выбранного угла — это 422 от бэка (files.*.pages required|min:1),
  // delete-pages без удалённых страниц вернул бы исходный файл.
  const nothingChanged = !!requireChange && (
    !pdfFiles.length
    || pdfFiles.every((pdfFile) => !(pdfFile[requireChange] || []).some(Boolean))
  );

  const changeHint = nothingChanged
    ? t(`convert-and-edit:editing.require-${requireChange === 'rotations' ? 'rotation' : 'deletion'}`)
    : null;

  const handleCardClick = useCallback((event, { fileIndex, pages }) => {
    if (selectable) {
      if (viewType === 'files') {
        selectFile(fileIndex);
      } else {
        selectPages({ [fileIndex]: pages });
      }
    }
  }, [selectable, viewType, selectPages, selectFile]);

  const handlePagesView = useCallback(() => {
    sendEvent({
      category: 'pdf-viewer',
      action: 'change-view',
      label: 'pages',
    });

    setViewType('pages');
  }, []);

  const handleFilesView = useCallback(() => {
    sendEvent({
      category: 'pdf-viewer',
      action: 'change-view',
      label: 'files',
    });

    setViewType('files');
  }, []);

  const handleSeparateCheckboxToggle = useCallback(() => {
    setSeparate((separate) => !separate);
  }, []);

  const handleDelete = useCallback(({ fileIndex, pages, viewType }) => {
    sendEvent({
      category: 'pdf-viewer',
      action: 'delete',
      label: viewType === 'files' ? 'file' : 'page',
    });

    deletePages(fileIndex, pages);
  }, [deletePages]);

  const handleRotateAnticlockwise = useCallback(({ fileIndex, pages }) => {
    sendEvent({
      category: 'pdf-viewer',
      action: 'rotate',
      label: 'anticlockwise',
    });

    rotatePages(fileIndex, pages, -90);
  }, [rotatePages]);

  const handleRotateClockwise = useCallback(({ fileIndex, pages }) => {
    sendEvent({
      category: 'pdf-viewer',
      action: 'rotate',
      label: 'clockwise',
    });

    rotatePages(fileIndex, pages, 90);
  }, [rotatePages]);

  const handleRotateAllAnticlockwise = useCallback(() => {
    sendEvent({
      category: 'pdf-viewer',
      action: 'rotate-all',
      label: 'anticlockwise',
    });

    rotateAllPages(-90);
  }, [rotateAllPages]);

  const handleRotateAllClockwise = useCallback(() => {
    sendEvent({
      category: 'pdf-viewer',
      action: 'rotate-all',
      label: 'clockwise',
    });

    rotateAllPages(90);
  }, [rotateAllPages]);

  const handleSelectionInputChange = useCallback((event) => {
    setSelectionInputValue(event.target.value);
  }, []);

  const handleSelectionInputBlur = useCallback((event) => {
    const chunkedPageRange = chunkPageRange(
      parsePageRange(event.target.value, totalPages),
      pdfFiles.map((pdfFile) => pdfFile.file.pdfDocument.numPages),
    );

    const selection = {};

    chunkedPageRange.forEach((pages, fileIndex) => {
      selection[fileIndex] = pages;
    });

    selectPages(selection, true);
  }, [pdfFiles, totalPages, selectPages]);

  const handleClickCTAButton = useCallback(() => {
    sendEvent({
      category: 'pdf-viewer',
      action: 'button-click',
      label: pdfFiles.length.toString(),
    });

    onSubmit?.({
      pdfFiles,
      separate,
    });
  }, [onSubmit, pdfFiles, separate]);

  useImperativeHandle(ref, () => ({
    submit: () => {
      // авто-сабмит после переотправки файла не должен обходить requireChange
      if (!nothingChanged && pdfFiles.every((pdfFile) => pdfFile.file.hash)) {
        onSubmit?.({
          pdfFiles,
          separate,
        });

        return true;
      }

      return false;
    },
  }));

  useSmallFooter();

  useEffect(() => {
    const layout = document.querySelector('.layout');

    layout.classList.add('page-pdf-viewer');

    return () => {
      layout.classList.remove('page-pdf-viewer');
    };
  }, []);

  useEffect(() => {
    const pageRange = flatChunkedPageRange(
      pdfFiles.map((pdfFile) => pdfFile.selections),
      pdfFiles.map((pdfFile) => pdfFile.file.pdfDocument.numPages),
    );

    setSelectionInputValue(stringifyPageRange(pageRange));
  }, [pdfFiles]);

  const cards = [];

  pdfFiles.forEach((pdfFile, i) => {
    const renderPageCount = viewType === 'files' ? 1 : pdfFile.file.pdfDocument.numPages;

    for (let pageNumber = 1; pageNumber <= renderPageCount; pageNumber++) {
      if (!pdfFile.deletions.includes(pageNumber) || viewType === 'files') {
        cards.push((
          <PdfCard
            key={`${pdfFile.file.key}:${pageNumber}`}
            fileIndex={i}
            pdfDocument={pdfFile.file.pdfDocument}
            pagePromise={pdfFile.pages[pageNumber - 1]}
            viewType={viewType}
            filename={pdfFile.file.file.name}
            group={pdfFiles.length >= 2 ? i + 1 : null}
            zoom={zoom}
            deletable={(
              viewType === 'files'
                ? deletableFiles
                : deletablePages
            )}
            rotatable={rotatable}
            rotation={pdfFile.rotations[pageNumber - 1]}
            selected={(
              viewType === 'files'
                ? pdfFile.selections.length > 0
                : pdfFile.selections.includes(pageNumber)
            )}
            onClick={handleCardClick}
            onRotateAnticlockwise={handleRotateAnticlockwise}
            onRotateClockwise={handleRotateClockwise}
            onDelete={handleDelete}
          />
        ));
      }
    }
  });

  return (
    <div
      className={classNames(
        'pdf-viewer',
        loading && 'pdf-viewer--loading',
      )}
    >
      <div className="pdf-viewer__header">
        <Container className="pdf-viewer__header__container">
          <h1 className="pdf-viewer__title">
            {title}
          </h1>
        </Container>
      </div>
      <div className="pdf-viewer__working-area">
        {!processing ? (
          <>
            <Container className="pdf-viewer__content-container">
              <div className="pdf-viewer__content">
                <div className="pdf-viewer__controls">
                  {!!dropzoneProps && (
                    <Dropzone
                      className="pdf-viewer__mobile-dropzone"
                      type="pdf"
                      onlyButton
                      buttonProps={{
                        variant: 'secondary',
                      }}
                      buttonText={t('convert-and-edit:dropzone.button-more')}
                      {...dropzoneProps}
                    />
                  )}
                  <div className="pdf-viewer__zoom pdf-viewer__button-group">
                    <button
                      className="pdf-viewer__button"
                      type="button"
                      aria-label={t('convert-and-edit:editing.zoom-in')}
                      onClick={zoomIn}
                    >
                      <IconZoomIn aria-hidden="true" />
                    </button>
                    <button
                      className="pdf-viewer__button"
                      type="button"
                      aria-label={t('convert-and-edit:editing.zoom-out')}
                      onClick={zoomOut}
                    >
                      <IconZoomOut aria-hidden="true" />
                    </button>
                  </div>
                  {showViewSwitcher && (
                    <Grid className="pdf-viewer__view-switcher my-xs-1" sizes={{ xs: [1, 2] }}>
                      <Grid.Item>
                        <button
                          className={classNames(
                            'pdf-viewer__button',
                            viewType === 'pages' && 'pdf-viewer__button--active',
                          )}
                          type="button"
                          onClick={handlePagesView}
                        >
                          <IconPages aria-hidden="true" />
                          <span>
                            {t('convert-and-edit:editing.pages')}
                          </span>
                        </button>
                      </Grid.Item>
                      <Grid.Item>
                        <button
                          className={classNames(
                            'pdf-viewer__button',
                            viewType === 'files' && 'pdf-viewer__button--active',
                          )}
                          type="button"
                          onClick={handleFilesView}
                        >
                          <IconFiles aria-hidden="true" className="pdf-viewer__svg"/>
                          <span>
                            {t('convert-and-edit:editing.files')}
                          </span>
                        </button>
                      </Grid.Item>
                    </Grid>
                  )}
                </div>
                <div className="pdf-viewer__cards">
                  <PdfCardList className="pdf-viewer__card-list">
                    {cards}
                  </PdfCardList>
                </div>
              </div>
              {separatable && (
                <div className="pdf-viewer__pre-footer">
                  <Checkbox
                    inputProps={{
                      checked: separate,
                      onChange: handleSeparateCheckboxToggle,
                    }}
                  >
                    {t('convert-and-edit:editing.save-as-separate-pdf')}
                  </Checkbox>
                </div>
              )}
            </Container>
            <Container className="pdf-viewer__footer-container">
              <div className="pdf-viewer__footer">
                {!!dropzoneProps && (
                  <div className="pdf-viewer__footer__left">
                    <Dropzone
                      className="pdf-viewer__dropzone"
                      type="pdf"
                      onlyButton
                      buttonProps={{
                        variant: 'secondary',
                      }}
                      buttonText={t('convert-and-edit:dropzone.button-more')}
                      {...dropzoneProps}
                    />
                  </div>
                )}
                <div className="pdf-viewer__footer__right">
                  {selectable && (
                    <Field className="pdf-viewer__selector-field" size="lg">
                      <Field.Input
                        value={selectionInputValue}
                        onChange={handleSelectionInputChange}
                        onBlur={handleSelectionInputBlur}
                        disabled={!files.length}
                        placeholder={t('convert-and-edit:editing.pages-selector', {
                          example: '3-5, 8, 12',
                        })}
                      />
                    </Field>
                  )}
                  {rotatable && (
                    <div className="pdf-viewer__rotation">
                      <button
                        className="pdf-viewer__rotation__button"
                        type="button"
                        aria-label={t('editing.rotate-anticlockwise')}
                        disabled={!files.length}
                        onClick={handleRotateAllAnticlockwise}
                      >
                        <IconRotateAnticlockwise aria-hidden="true" />
                      </button>
                      <button
                        className="pdf-viewer__rotation__button"
                        type="button"
                        aria-label={t('editing.rotate-clockwise')}
                        disabled={!files.length}
                        onClick={handleRotateAllClockwise}
                      >
                        <IconRotateClockwise aria-hidden="true" />
                      </button>
                    </div>
                  )}
                  {/* обёртка нужна, чтобы подсказка (title) показывалась:
                      на disabled-кнопке браузер её не покажет */}
                  <span className="pdf-viewer__cta" title={changeHint || undefined}>
                    <Button
                      className="pdf-viewer__cta-button"
                      size="lg"
                      variant="primary"
                      disabled={disabled || !files.length || nothingChanged}
                      onClick={handleClickCTAButton}
                    >
                      {title}
                    </Button>
                  </span>
                </div>
              </div>
            </Container>
          </>
        ) : (
          <Container className="pdf-viewer__loader-container">
            <div className="pdf-viewer__loader">
              <IconLoader
                className="pdf-viewer__loader__icon"
                aria-hidden="true"
              />
              {t('convert-and-edit:editing.loader')}
            </div>
          </Container>
        )}
      </div>
    </div>
  );
});

PdfViewer.displayName = 'PdfViewer';

PdfViewer.propTypes = {
  title: PropTypes.string.isRequired,
  files: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    file: PropTypes.shape({
      name: PropTypes.string.isRequired,
      size: PropTypes.number.isRequired,
    }).isRequired,
    pdfDocument: PropTypes.object.isRequired,
    password: PropTypes.string,
    hash: PropTypes.string,
  })).isRequired,
  setFiles: PropTypes.func.isRequired,
  viewType: PropTypes.oneOf([
    'pages',
    'files',
  ]),
  showViewSwitcher: PropTypes.bool,
  separatable: PropTypes.bool,
  selectable: PropTypes.bool,
  rotatable: PropTypes.bool,
  deletableFiles: PropTypes.bool,
  deletablePages: PropTypes.bool,
  requireChange: PropTypes.oneOf(['rotations', 'deletions']),
  loading: PropTypes.bool,
  processing: PropTypes.bool,
  dropzoneProps: PropTypes.object,
  disabled: PropTypes.bool,
  onSubmit: PropTypes.func,
};

export default PdfViewer;
